import { GraduationCap } from 'lucide-react';
import dayjs from '../lib/dates';
import type { ListedConversation } from '../lib/unifiedMessaging';
import { KIND_LABELS, initials } from './StudentMessageParts';
import { StaffPhoto, StudentPhoto } from './Photos';
import { PresenceDot, PresenceText } from './Presence';

/** Nature d'une conversation de l'équipe, telle que le serveur la rend. */
export const TEAM_LABELS: Record<string, string> = {
  DIRECT: 'Message direct',
  GROUP: 'Groupe privé',
  SCHOOL: 'Salon école',
  AGENCY: 'Salon agence',
};

/** Heure pour aujourd'hui, date sinon. */
function listStamp(at: number): string {
  const date = dayjs(at);
  const now = dayjs();
  if (date.isSame(now, 'day')) return date.format('HH:mm');
  return date.format(date.isSame(now, 'year') ? 'D MMM' : 'DD/MM/YYYY');
}

/**
 * Liste unique de l'écran « Messages » : conversations de l'équipe et
 * conversations avec des élèves, déjà triées. Une conversation où un élève lit
 * porte toujours sa marque (« Élève », « Groupe avec élèves »).
 */
export default function ConversationList({
  items,
  onOpen,
}: {
  items: ListedConversation[];
  onOpen: (conversation: ListedConversation) => void;
}) {
  if (!items.length) return null;
  return (
    <ul aria-label="Conversations" className="messaging-list">
      {items.map((c) => (
        <li key={`${c.origin}:${c.id}`}>
          <button type="button" onClick={() => onOpen(c)}>
            {/* Le nom d'abord dans le document, pour qui écoute la liste ; l'avatar est replacé à gauche par la feuille de style. */}
            <span className="messaging-list-copy">
              <strong>
                {c.title}
                <PresenceText presence={c.presence} />
                <span className="messaging-sr-only">
                  {' '}
                  · {c.unread} non lu(s)
                </span>
              </strong>
              {c.origin === 'student' ? (
                <>
                  <small>
                    <span className="messaging-student-mark">
                      <GraduationCap size={12} aria-hidden="true" />
                      {KIND_LABELS[c.kind] ?? 'Élève'}
                    </span>
                  </small>
                  <small className="messaging-list-last">
                    {c.last
                      ? `${c.last.author} : ${c.last.body}`
                      : 'Aucun message'}
                  </small>
                </>
              ) : (
                <small>{TEAM_LABELS[c.type ?? ''] ?? 'Équipe'}</small>
              )}
            </span>
            <span
              aria-hidden="true"
              className={`messaging-avatar${
                c.origin === 'student' ? ' messaging-avatar--student' : ''
              }`}
            >
              {initials(c.title)}
              {c.origin === 'student' ? (
                <StudentPhoto candidateId={c.candidateId} />
              ) : (
                <StaffPhoto userId={c.peerId} />
              )}
              <PresenceDot presence={c.presence} />
            </span>
            <span className="messaging-list-meta">
              {c.at > 0 && <span>{listStamp(c.at)}</span>}
              {c.unread > 0 && (
                <span className="messaging-unread" aria-hidden="true">
                  {c.unread > 99 ? '99+' : c.unread}
                </span>
              )}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
